import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from '../cx';

export function Card({
  className,
  interactive,
  selected,
  ...rest
}: { interactive?: boolean; selected?: boolean } & HTMLAttributes<HTMLDivElement>) {
  return <div className={cx('rr-card', interactive && 'rr-card--link', selected && 'rr-card--selected', className)} {...rest} />;
}

export function DashedCard({ children, className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cx('rr-card rr-card--dashed', className)} {...rest}>
      {children}
    </div>
  );
}

export function StatusDot({ tone = 'idle' }: { tone?: 'idle' | 'active' | 'up' | 'down' | 'hollow' }) {
  return <span className={cx('rr-dot', tone !== 'idle' && `rr-dot--${tone}`)} aria-hidden="true" />;
}

export function Tag({ children, tone, className }: { children: ReactNode; tone?: 'accent' | 'outline'; className?: string }) {
  return <span className={cx('rr-tag', tone && `rr-tag--${tone}`, className)}>{children}</span>;
}

export interface ProjectCardProps {
  organization: string;
  name: string;
  statusLabel: string;
  statusTone?: 'idle' | 'active' | 'up' | 'down' | 'hollow';
  cycleLabel?: string;
}

/** REGINA · Ciclo 01 · VER · ● em andamento */
export function ProjectCard({ organization, name, cycleLabel, statusLabel, statusTone = 'idle' }: ProjectCardProps) {
  return (
    <div className="rr-project-card">
      <div>
        <div className="rr-project-card__org">{organization.toUpperCase()}</div>
        <div className="rr-project-card__name">{cycleLabel ? `${cycleLabel} · ${name}` : name}</div>
      </div>
      <div className="rr-project-card__status">
        <StatusDot tone={statusTone} />
        <span>{statusLabel}</span>
      </div>
    </div>
  );
}

export function StatTile({ value, label }: { value: ReactNode; label: string }) {
  return (
    <div className="rr-stat">
      <div className="rr-stat__value">{value}</div>
      <div className="rr-stat__label">{label}</div>
    </div>
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="rr-empty">
      <div className="rr-empty__title">{title}</div>
      {children && <div style={{ fontSize: 'var(--rr-text-sm)' }}>{children}</div>}
    </div>
  );
}

export function Notice({ children, tone }: { children: ReactNode; tone?: 'error' }) {
  return <div className={cx('rr-notice', tone === 'error' && 'rr-notice--error')}>{children}</div>;
}
